import type { Segment } from '../domains/global-filters/model/segment';
import { getDefaultDateRange, getDefaultFilterInQueryFormat } from '../constant';
import type { FilterType, GlobalFilter } from '../domains/global-filters/model/FilterChain';
import { convertFromQueryToNewUiFormat } from './FormatConverter/FilterConverterFromQueryToUi';
import type { DateRange } from '../components/StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';
import type { QueryFormatFilter } from '../domains/global-filters/model/QueryFormat';

const FILTER_QUERY_PARAM_KEY = 'filters';
const FILTER_SERIES_QUERY_PARAM_KEY = 'filter-series';
const STEPS_QUERY_PARAM_KEY = 'steps';
const SEGMENT_QUERY_PARAM_KEY = 'segment';
const DATE_RANGE_QUERY_PARAM_KEY = 'date';

export function readIndexedFiltersFromUrl(
  segments: Segment[],
  index: number,
): {
  filtersInUiFormat: GlobalFilter;
  segmentId?: number;
  isDefaultValue: boolean;
} {
  const url = new URL(window.location.href);
  const rawFiltersURLParams = url.searchParams.get(
    `${FILTER_QUERY_PARAM_KEY}${index === 0 ? '' : index + 1}`,
  );
  const rawSegmentIdURLParams = url.searchParams.get(
    `${SEGMENT_QUERY_PARAM_KEY}${index === 0 ? '' : index + 1}`,
  );
  let segmentId;
  let filtersInQueryFormat = getDefaultFilterInQueryFormat();
  if (rawFiltersURLParams != null) {
    // As url.searchParams is already decoded we don't need to call decodeURIComponent
    filtersInQueryFormat = JSON.parse(rawFiltersURLParams);
  } else if (rawSegmentIdURLParams != null) {
    const currentSegmentId = parseInt(rawSegmentIdURLParams, 10) || -1;
    const newSegment = segments.find((segment) => segment.id === currentSegmentId);
    if (newSegment) {
      segmentId = newSegment.id;
      filtersInQueryFormat = newSegment.filters;
    }
  }
  return {
    filtersInUiFormat: convertFromQueryToNewUiFormat(filtersInQueryFormat),
    segmentId,
    isDefaultValue: rawFiltersURLParams == null,
  };
}

export function readFiltersFromUrl(segments: Segment[]): {
  filtersInUiFormat: GlobalFilter;
  segmentId?: number;
  isDefaultValue: boolean;
} {
  return readIndexedFiltersFromUrl(segments, 0);
}

export function readFilterSeriesFromUrl(): {
  filterSeriesInUiFormat: FilterType[][];
  isDefaultValue: boolean;
} {
  const url = new URL(window.location.href);
  const rawFilterSeriesURLParams = url.searchParams.get(FILTER_SERIES_QUERY_PARAM_KEY);
  const useDefaultValue = rawFilterSeriesURLParams == null;
  return {
    filterSeriesInUiFormat: useDefaultValue
      ? [[]]
      : JSON.parse(rawFilterSeriesURLParams).map(
          (item: QueryFormatFilter) => convertFromQueryToNewUiFormat(item).filterList,
        ),
    isDefaultValue: useDefaultValue,
  };
}

export function readDateRangeFromUrl(): {
  dateRange: DateRange;
  isDefaultValue: boolean;
} {
  const url = new URL(window.location.href);
  const rawDateURLParams = url.searchParams.get(DATE_RANGE_QUERY_PARAM_KEY);
  const useDefaultValue = rawDateURLParams == null;
  const defaultDateRange = getDefaultDateRange();
  return {
    dateRange: useDefaultValue
      ? defaultDateRange
      : convertFromQueryToNewUiFormat(JSON.parse(rawDateURLParams)).dateRange ?? defaultDateRange,
    isDefaultValue: useDefaultValue,
  };
}

export function readStepsFromUrl(): {
  stepsList: FilterType[][];
  isDefaultValue: boolean;
} {
  const url = new URL(window.location.href);
  const rawStepsURLParams = url.searchParams.get(STEPS_QUERY_PARAM_KEY);
  const useDefaultValue = rawStepsURLParams == null;
  return {
    stepsList: useDefaultValue
      ? [[], []]
      : JSON.parse(rawStepsURLParams).map(
          (item: QueryFormatFilter) => convertFromQueryToNewUiFormat(item).filterList,
        ),
    isDefaultValue: useDefaultValue,
  };
}
